<template>
  <div class="reaction-bar" role="group" aria-label="Post reactions">
    <button
      v-for="emoji in EMOJI_ALLOWLIST"
      :key="emoji"
      type="button"
      class="reaction-btn"
      :class="{ 'reaction-btn--active': reactionState[emoji]?.reacted_by_me }"
      :aria-label="`React with ${emoji} (${reactionState[emoji]?.count ?? 0})`"
      :aria-pressed="reactionState[emoji]?.reacted_by_me ?? false"
      @click="toggleReaction(emoji)"
    >
      <span class="reaction-btn__emoji">{{ emoji }}</span>
      <span v-if="reactionState[emoji]?.count" class="reaction-btn__count">
        {{ reactionState[emoji].count }}
      </span>
    </button>

    <span v-if="errorMessage" class="reaction-error" role="alert">
      {{ errorMessage }}
    </span>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from '@/axios-instance'

const EMOJI_ALLOWLIST = ['👍', '👎', '❤️', '😂', '😮', '😢']

const props = defineProps({
  postId: {
    type: [Number, String],
    required: true,
  },
  threadId: {
    type: [Number, String],
    required: true,
  },
})

// { emoji: { count, reacted_by_me } }
const reactionState = ref({})
const errorMessage = ref(null)

let errorTimer = null

function showError(msg) {
  errorMessage.value = msg
  clearTimeout(errorTimer)
  errorTimer = setTimeout(() => {
    errorMessage.value = null
  }, 3000)
}

async function fetchReactions() {
  try {
    const res = await axios.get(`/api/v1/discussions/threads/${props.threadId}/posts/${props.postId}/reactions/`)
    const map = {}
    for (const item of res.data) {
      map[item.emoji] = { count: item.count, reacted_by_me: item.reacted_by_me }
    }
    reactionState.value = map
  } catch (err) {
    console.error('Failed to fetch reactions', err)
  }
}

async function toggleReaction(emoji) {
  const current = reactionState.value[emoji] ?? { count: 0, reacted_by_me: false }
  const wasReacted = current.reacted_by_me

  // Optimistic update
  const optimisticCount = wasReacted ? Math.max(0, current.count - 1) : current.count + 1
  reactionState.value = {
    ...reactionState.value,
    [emoji]: { count: optimisticCount, reacted_by_me: !wasReacted },
  }

  try {
    const res = await axios.post(`/api/v1/discussions/threads/${props.threadId}/posts/${props.postId}/react/`, { emoji })
    // Merge server-returned counts into state
    const serverReactions = res.data.reactions ?? {}
    const updated = { ...reactionState.value }
    for (const [e, count] of Object.entries(serverReactions)) {
      updated[e] = {
        count,
        reacted_by_me: e === emoji ? !wasReacted : (updated[e]?.reacted_by_me ?? false),
      }
    }
    reactionState.value = updated
  } catch (err) {
    console.error('Failed to react', err)
    // Revert optimistic update
    reactionState.value = {
      ...reactionState.value,
      [emoji]: { count: current.count, reacted_by_me: wasReacted },
    }
    const msg = err.response?.data?.detail || 'Could not save reaction. Please try again.'
    showError(msg)
  }
}

onMounted(() => {
  fetchReactions()
})
</script>

<style scoped>
.reaction-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.3rem;
  position: relative;
}

.reaction-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.5rem;
  border: 1px solid var(--gray-200);
  border-radius: 9999px;
  background: white;
  cursor: pointer;
  font-size: 0.85rem;
  line-height: 1.4;
  transition: border-color 0.15s, background 0.15s;
  font-family: inherit;
}

.reaction-btn:hover {
  border-color: var(--halsa-green);
  background: var(--gray-100);
}

.reaction-btn:focus-visible {
  outline: 2px solid var(--halsa-green);
  outline-offset: 2px;
}

.reaction-btn--active {
  border-color: var(--halsa-green);
  background: color-mix(in srgb, var(--halsa-green) 12%, white);
  font-weight: 600;
}

.reaction-btn__emoji {
  font-size: 1rem;
  line-height: 1;
}

.reaction-btn__count {
  color: var(--text-black);
  font-size: 0.78rem;
  min-width: 0.8rem;
  text-align: center;
}

.reaction-btn--active .reaction-btn__count {
  color: var(--halsa-green);
}

.reaction-error {
  font-size: 0.78rem;
  color: var(--halsa-red);
  margin-left: 0.25rem;
}
</style>
